import { State } from '@progress/kendo-data-query';
import { isWithinInterval } from 'date-fns';
import { TFunction } from 'i18next';
import { sumBy } from 'lodash';
import {
  createContext,
  Dispatch,
  FC,
  ReactNode,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import useCompany from 'src/hooks/useCompany';
import { useGetAllGlCodeQuery } from 'src/services/gl_code/useGetAllGlCodeQuery';
import useGetAccrualReportApi, {
  AccrualReportResponse,
  FormattedReportResponse,
} from 'src/services/useGetAccrualReportApi';
import useGlCodeDepartmentsApi, {
  GLCodeDepartment,
  GLCodeDepartmentsResponse,
} from 'src/services/useGlCodeDepartmentsApi';
import useGLPeriodApi, {
  GLPeriodResponse,
  GLPeriodResponseObject,
  GLPeriods,
} from 'src/services/useGLPeriodApi';
import { Company } from 'src/types/Companies';
import { SetState } from 'src/types/generic';
import { GlCodeResponse } from 'src/types/GLCode';
import { removeTime } from 'src/utils/formatTime';
import {
  formatGLPeriodDropDown,
  GLPeriodDropdown,
  GLPeriodDropdownChildren,
} from 'src/utils/transformation';

export const initialDepartmentState: State = {
  skip: 0,
  take: 350,
  sort: [{ field: 'name', dir: 'asc' }],
  filter: {
    logic: 'and',
    filters: [],
  },
};

export const initialPeriodDataState: State = {
  skip: 0,
  take: 5000,
  sort: [{ field: 'code', dir: 'asc' }],
  filter: {
    logic: 'and',
    filters: [],
  },
};

interface ViewAccrualContextType {
  glCodes: GlCodeResponse[];
  selectedCompany: Company;
  selectedDepartment: GLCodeDepartment;
  setSelectedDepartment: SetState<GLCodeDepartment>;
  selectedGlcode: any;
  setSelectedGlcode: Dispatch<SetStateAction<any>>;
  selectedGlperiod: any;
  setSelectedGlperiod: Dispatch<SetStateAction<any>>;
  periodDataState: State;
  setPeriodDataState: SetState<State>;
  excelLoading: boolean;
  setExcelLoading: SetState<boolean>;
  departmentList: GLCodeDepartment[];
  glCodeDepartments: GLCodeDepartmentsResponse[];
  t: TFunction<'translation', undefined>;
  companyId: string;
  glCodeList: any[];
  glPeriodList: any[];
  glPeriods: GLPeriods;
  getGLPeriods: (params?: undefined) => Promise<GLPeriodResponse>;
  glPeriodIdByPeriod?: number;
  dropDownPeriodValue: GLPeriodDropdownChildren | null;
  setDropDownPeriodValue: SetState<GLPeriodDropdownChildren | null>;
  dropDownPeriodValues: GLPeriodDropdown[];
  isDropDownLoading: boolean;
  isLeftBtnDropDwnDisabled: boolean;
  isRightBtnDropDwnDisabled: boolean;
  onLeftBtnDropDwnClick: VoidFunction;
  onRightBtnDropDwnClick: VoidFunction;
  accrualReport: FormattedReportResponse[];
  getAccrualReport: (params?: undefined) => Promise<FormattedReportResponse[]>;
  setAccrualReport: SetState<FormattedReportResponse[]>;
  isAccrualLoading: boolean;
  totals: {
    grniItemCount: number;
    grniDocCount: number;
    poTotal: number;
    amountInPeriod: number;
    accrualAmount: number;
  };
  rawAccrualData: AccrualReportResponse[];
}

export const ViewAccrualContext = createContext<ViewAccrualContextType>(
  {} as ViewAccrualContextType
);

interface ViewAccrualContextProps {
  children: ReactNode;
}
export interface SelectedDepartment {
  departmentId: number;
  departmentName: string;
}
const defaultDepartment = { glCdId: 0, departmentId: 0, departmentName: 'All Departments' };
const defaultGLCode = { glCd: 'All', glCdId: 0 };
const defaultGLPeriod = { period: 'All', glPeriodId: 0 };
const currentDate = removeTime(new Date());

const ViewAccrualContextProvider: FC<ViewAccrualContextProps> = ({ children }) => {
  const { company: selectedCompany } = useCompany();
  const { companyId } = selectedCompany;
  const { t } = useTranslation();
  const [selectedDepartment, setSelectedDepartment] = useState(defaultDepartment);
  const [selectedGlcode, setSelectedGlcode] = useState(defaultGLCode);
  const [selectedGlperiod, setSelectedGlperiod] = useState(defaultGLPeriod);
  const [periodDataState, setPeriodDataState] = useState<State>({
    ...initialPeriodDataState,
  });
  const [dropDownPeriodValue, setDropDownPeriodValue] = useState<GLPeriodDropdownChildren | null>(
    null
  );
  const { getGlCodeDepartments, glCodeDepartments } = useGlCodeDepartmentsApi(companyId);
  const { data: glCodes = [] } = useGetAllGlCodeQuery(companyId);
  const { glPeriods, isLoading: isDropDownLoading, getGLPeriods } = useGLPeriodApi(companyId);
  const dropDownPeriodValues = useMemo(() => formatGLPeriodDropDown(glPeriods), [glPeriods]);
  const [excelLoading, setExcelLoading] = useState(false);
  const uniqueDepartments = useMemo(
    () =>
      glCodeDepartments?.filter((item) => {
        for (let code of glCodes) {
          if (code.id === item.glCdId) {
            return true;
          }
          return false;
        }
        return false;
      }),
    [glCodeDepartments, glCodes]
  );

  const departmentList = useMemo(
    () => (uniqueDepartments ? [defaultDepartment, ...uniqueDepartments] : [defaultDepartment]),
    [uniqueDepartments]
  );
  const glPeriodList = useMemo(
    () => (glPeriods ? [defaultGLPeriod, ...glPeriods] : [defaultGLPeriod]),
    [glPeriods]
  );
  const glPeriodIdByPeriod = useMemo(() => {
    const { glPeriodId = undefined } = dropDownPeriodValue ?? ({} as GLPeriodResponseObject);
    return glPeriodId;
  }, [dropDownPeriodValue]);
  const {
    accrualReport,
    getAccrualReport,
    setAccrualReport,
    isLoading: isAccrualLoading,
    rawAccrualData,
  } = useGetAccrualReportApi(companyId, glPeriodIdByPeriod ?? 0);
  const glCodeList = useMemo(
    () => (accrualReport ? [defaultGLCode, ...accrualReport] : [defaultGLCode]),
    [accrualReport]
  );
  const fiscalYearIndex = useMemo(
    () =>
      dropDownPeriodValues?.findIndex(
        (value) => value.fiscalYear === dropDownPeriodValue?.fiscalYear
      ),
    [dropDownPeriodValue?.fiscalYear, dropDownPeriodValues]
  );

  const currentPeriods = useMemo(
    () => dropDownPeriodValues[fiscalYearIndex]?.items as GLPeriodDropdownChildren[],
    [dropDownPeriodValues, fiscalYearIndex]
  );

  const periodIndex = useMemo(
    () =>
      currentPeriods?.findIndex((period) => period.glPeriodId === dropDownPeriodValue?.glPeriodId),
    [currentPeriods, dropDownPeriodValue?.glPeriodId]
  );

  const isLeftBtnDropDwnDisabled: boolean = useMemo(() => {
    if (fiscalYearIndex === dropDownPeriodValues?.length - 1 && periodIndex === 0) {
      return true;
    } else return false;
  }, [dropDownPeriodValues?.length, fiscalYearIndex, periodIndex]);

  const isRightBtnDropDwnDisabled: boolean = useMemo(() => {
    if (fiscalYearIndex === 0 && periodIndex === currentPeriods?.length - 1) {
      return true;
    }
    return false;
  }, [currentPeriods?.length, fiscalYearIndex, periodIndex]);

  const onLeftBtnDropDwnClick = useCallback(() => {
    setDropDownPeriodValue((prev) => {
      if (periodIndex === 0) {
        const previousPeriod = dropDownPeriodValues[fiscalYearIndex + 1].items;
        const newValue = previousPeriod[previousPeriod?.length - 1];
        return newValue;
      } else {
        const newValue = currentPeriods[periodIndex - 1];
        return newValue;
      }
    });
    setPeriodDataState((prev) => ({
      ...initialPeriodDataState,
      take: prev.take,
    }));
  }, [currentPeriods, dropDownPeriodValues, fiscalYearIndex, periodIndex]);

  const onRightBtnDropDwnClick = useCallback(() => {
    setDropDownPeriodValue((prev) => {
      if (periodIndex === currentPeriods?.length - 1) {
        const newValue = dropDownPeriodValues[fiscalYearIndex - 1].items[0];
        return newValue;
      } else {
        const newValue = currentPeriods[periodIndex + 1];
        return newValue;
      }
    });

    setPeriodDataState((prev) => ({
      ...initialPeriodDataState,
      take: prev.take,
    }));
  }, [currentPeriods, dropDownPeriodValues, fiscalYearIndex, periodIndex]);

  useEffect(() => {
    if (companyId) {
      getGLPeriods();
      getGlCodeDepartments();
    }
  }, [companyId, getGLPeriods, getGlCodeDepartments]);

  useEffect(() => {
    if (companyId && dropDownPeriodValue) {
      getAccrualReport();
    }
  }, [companyId, dropDownPeriodValue, getAccrualReport]);

  useEffect(() => {
    setDropDownPeriodValue((prev) => {
      let newValue: GLPeriodDropdownChildren | null = null;
      const initialDropdownValue =
        glPeriods.find((value) =>
          isWithinInterval(currentDate, {
            start: removeTime(new Date(value.startDate)),
            end: removeTime(new Date(value.endDate)),
          })
        ) ?? null;
      let prevValue = dropDownPeriodValues
        ?.find((value) => value.fiscalYear === prev?.fiscalYear)
        ?.items.find((value) => value.glPeriodId === prev?.glPeriodId) as GLPeriodDropdownChildren;
      if (prev && prevValue) {
        newValue = prevValue;
      } else if (initialDropdownValue) {
        newValue = dropDownPeriodValues
          ?.find((value) => value.fiscalYear === initialDropdownValue?.fiscalYear)
          ?.items.find(
            (value) => value.glPeriodId === initialDropdownValue.glPeriodId
          ) as GLPeriodDropdownChildren;
      }
      return newValue;
    });
  }, [dropDownPeriodValues, glPeriods]);

  const totals = useMemo(
    () => ({
      grniItemCount: sumBy(accrualReport, 'grniItemCount'),
      grniDocCount: sumBy(accrualReport, 'grniDocCount'),
      poTotal: sumBy(accrualReport, 'poTotal'),
      amountInPeriod: sumBy(accrualReport, 'amountInPeriod'),
      accrualAmount: sumBy(accrualReport, 'accrualAmount'),
    }),
    [accrualReport]
  );

  return (
    <ViewAccrualContext.Provider
      value={{
        glCodes,
        selectedCompany,
        excelLoading,
        setExcelLoading,
        t,
        companyId,
        selectedDepartment,
        setSelectedDepartment,
        departmentList,
        glCodeDepartments,
        glCodeList,
        selectedGlcode,
        setSelectedGlcode,
        glPeriods,
        getGLPeriods,
        glPeriodList,
        selectedGlperiod,
        setSelectedGlperiod,
        dropDownPeriodValue,
        setDropDownPeriodValue,
        dropDownPeriodValues,
        isDropDownLoading,
        setPeriodDataState,
        isLeftBtnDropDwnDisabled,
        isRightBtnDropDwnDisabled,
        onLeftBtnDropDwnClick,
        onRightBtnDropDwnClick,
        periodDataState,
        glPeriodIdByPeriod,
        accrualReport,
        getAccrualReport,
        setAccrualReport,
        isAccrualLoading,
        totals,
        rawAccrualData,
      }}
    >
      {children}
    </ViewAccrualContext.Provider>
  );
};

export default ViewAccrualContextProvider;
